import styled from '@emotion/styled'
import { DoseDispensingProductsListItemDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetCatalogItemChevron,
  ExtranetExpandArrowColumn,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  useBreakpoint,
} from '@pharmac-extranet/shared'
import { PharmacIcon, PharmacLink } from '@pharmac/ui'
import { CatalogItemAvailability } from './CatalogItemAvailability'
import { CatalogItemDetails } from './CatalogItemDetails'
import { CatalogItemHalving } from './CatalogItemHalving'

type DoseDispensingCatalogItemProps = {
  product: DoseDispensingProductsListItemDto
  handleSubstituteSearch: (v: string) => void
}

const ProductNumberColumnInner = styled.div<{ isExpanded: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  --max-height: calc(108px - (2 * var(--spacing-small)));

  position: relative;

  div {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  div.pnci--product-number {
    height: calc(var(--max-height) / 2);
    padding-top: calc(
      ${(p) => (p.isExpanded ? `var(--spacing-small) + 1px` : `var(--spacing-small)`)}
    );
    align-items: flex-start;
  }

  div.pnci--subst-text {
    height: calc(var(--max-height) / 2);
    align-items: flex-end;
    padding-bottom: var(--spacing-small);
  }

  div.pnci--icon {
    position: absolute;

    svg {
      width: 24px;
      height: 24px;
    }

    left: calc(50% - 12px);
    top: calc(var(--max-height) / 2 - 12px);
  }
`

export const DoseDispensingCatalogItem = ({
  product,
  handleSubstituteSearch,
}: DoseDispensingCatalogItemProps) => {
  const { minDesktop } = useBreakpoint()

  const hasSubstituteProduct = !!product.substituteProduct

  const ProductNumberColumn = ({ isExpanded }: { isExpanded?: boolean }) => {
    if (!hasSubstituteProduct || !minDesktop) return product.productNumber

    return (
      <ProductNumberColumnInner isExpanded={!!isExpanded}>
        <div className="pnci--product-number">{product.productNumber}</div>
        <div className="pnci--icon">{!isExpanded && <PharmacIcon icon="refreshCcw" />}</div>
        <div className="pnci--subst-text">{!isExpanded && 'Korvaava:'}</div>
      </ProductNumberColumnInner>
    )
  }

  return (
    <ExtranetTableRow
      key={product.id}
      expand={
        <ExtranetTableRow
          noBorders
          noMargin
          style={{
            borderTop: minDesktop ? 'none' : '1px solid var(--color-accent-4)',
          }}
        >
          {minDesktop && (
            <ExtranetTableColumn
              size={1.4}
              style={{
                minWidth: 105,
                maxWidth: 105,
                backgroundColor: hasSubstituteProduct
                  ? 'var(--extranet-light-purple)'
                  : 'var(--color-accent-3)',
              }}
            />
          )}
          <ExtranetTableGroupColumn size={minDesktop ? 14.5 : 15.9}>
            <CatalogItemDetails
              handleSubstituteSearch={handleSubstituteSearch}
              product={product}
            />
          </ExtranetTableGroupColumn>
        </ExtranetTableRow>
      }
      toggleExpand={() => null}
      children={({ isExpanded }) => (
        <>
          <ExtranetTableColumn
            size={1.4}
            flexJustify="center"
            style={{
              minWidth: 105,
              maxWidth: 105,
              backgroundColor: hasSubstituteProduct
                ? 'var(--extranet-light-purple)'
                : 'var(--color-accent-3)',
              fontWeight: 500,
            }}
          >
            <ProductNumberColumn isExpanded={isExpanded} />
          </ExtranetTableColumn>
          <ExtranetTableGroupColumn size={14.5}>
            <ExtranetTableRow
              expand
              noMargin
              noBorders
            >
              <ExtranetTableColumn
                size={3}
                paddingMediumHorizontal
                style={{ fontWeight: 500 }}
              >
                {product.name} {!minDesktop && product.strength}
              </ExtranetTableColumn>
              {minDesktop && (
                <>
                  <ExtranetTableColumn size={2}>{product.strength}</ExtranetTableColumn>
                  <ExtranetTableColumn size={3.5}>{product.activeSubstances}</ExtranetTableColumn>
                  <ExtranetTableColumn size={2}>{product.manufacturerName}</ExtranetTableColumn>
                  <ExtranetTableColumn size={2}>
                    <CatalogItemAvailability availability={product.availability} />
                  </ExtranetTableColumn>
                  <ExtranetTableColumn size={2}>
                    <CatalogItemHalving halving={product.halving} />
                  </ExtranetTableColumn>
                </>
              )}
              <ExtranetExpandArrowColumn>
                <ExtranetCatalogItemChevron isExpanded={isExpanded} />
              </ExtranetExpandArrowColumn>
            </ExtranetTableRow>
            {product.substituteProduct && minDesktop && !isExpanded && (
              <ExtranetTableRow
                noMargin
                noBorders
                style={{
                  borderTop: '2px dashed var(--color-accent-4)',
                }}
              >
                <ExtranetTableColumn
                  paddingMediumHorizontal
                  size={1.4}
                >
                  {product.substituteProduct.productNumber}
                </ExtranetTableColumn>
                <ExtranetTableColumn size={14}>
                  <PharmacLink
                    onClick={() =>
                      product.substituteProduct.productNumber &&
                      handleSubstituteSearch(product.substituteProduct.productNumber)
                    }
                    style={{ textDecoration: 'underline', fontWeight: 'normal' }}
                  >
                    {product.substituteProduct.name} {product.substituteProduct.strength}
                  </PharmacLink>
                </ExtranetTableColumn>
              </ExtranetTableRow>
            )}
          </ExtranetTableGroupColumn>
        </>
      )}
    />
  )
}
